import axios, { AxiosInstance } from 'axios';

import { getToken } from './users';

interface CustomAxiosInstance extends AxiosInstance {
  orgId?: string;
  spaceExternalId?: string;
}

let refreshTokenPromise: Promise<any> | null = null;

export const httpClient: CustomAxiosInstance =
  axios.create() as CustomAxiosInstance;

export const setBaseUrl = (baseUrl: string) => {
  httpClient.defaults.baseURL = baseUrl;
};

export const setOrgId = (orgId: string) => {
  httpClient.orgId = orgId;
};

export const setSpaceExternalId = (spaceExternalId: string) => {
  httpClient.spaceExternalId = spaceExternalId || undefined;
};

// Exported for unit testing.
// Match both /sign_in (app-level redirect) and /users/sign_in (Devise default)
// while rejecting false positives like /sign_in_with_provider.
export const isSignInRedirect = (finalUrl: string): boolean => {
  try {
    const { pathname } = new URL(finalUrl, window.location.origin);
    return pathname === '/sign_in' || pathname === '/users/sign_in';
  } catch {
    return false;
  }
};

// Axios interceptor to handle token expiration and refresh
httpClient.interceptors.response.use(
  response => {
    // Axios follows 302 redirects silently. Detect a redirect to the sign-in
    // page and surface it as a 401 so the error interceptor below can
    // refresh the token and retry.
    const finalUrl: string =
      (response.request as XMLHttpRequest)?.responseURL ?? '';
    if (isSignInRedirect(finalUrl)) {
      return Promise.reject(
        Object.assign(new Error('Session expired'), {
          response: { status: 401, config: response.config },
          config: response.config,
        }),
      );
    }
    return response;
  },
  async error => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      httpClient.orgId
    ) {
      originalRequest._retry = true; // Prevent infinite retries

      // If a refresh is already in progress, wait for it to complete
      if (!refreshTokenPromise) {
        refreshTokenPromise = getToken(
          httpClient.orgId,
          httpClient.spaceExternalId,
        );

        refreshTokenPromise.finally(() => {
          refreshTokenPromise = null; // Reset once the refresh is complete
        });
      }

      // Wait for the token refresh to complete
      try {
        const authResponse = await refreshTokenPromise;

        // Retry the original request with the new token
        originalRequest.headers[
          'Authorization'
        ] = `Bearer ${authResponse.token}`;
        httpClient.defaults.headers.common[
          'Authorization'
        ] = `Bearer ${authResponse.token}`;
        return httpClient(originalRequest);
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        return Promise.reject(refreshError); // Reject if refresh fails
      }
    }

    return Promise.reject(error);
  },
);

export const setAuthorizationHeader = (token: string | null) => {
  if (!token) {
    delete httpClient.defaults.headers.common['Authorization'];
    return;
  }

  httpClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
};
